// About.js - Displays company information with animated slideshow and feature cards
import { motion } from 'framer-motion';
import { Target, Eye, Heart } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import ENV from '../config/env';

const API_BASE = ENV.API_BASE;

const About = () => {
    const [content, setContent] = useState({
        features: [],
        stats: [],
        mainTitle: '',
        mainDescription: ''
    });
    const [loading, setLoading] = useState(true);

    // Load about content from server
    useEffect(() => {
        loadContent();
    }, []);

    const loadContent = async () => {
        try {
            const response = await fetch(`${API_BASE}/about`);
            if (response.ok) {
                const data = await response.json();
                setContent(data);
            }
        } catch (error) {
            console.error('Error loading about content:', error);
        } finally {
            setLoading(false);
        }
    };

    // Map icon names to components
    const getIconComponent = (iconName) => {
        const icons = { Target, Eye, Heart };
        return icons[iconName] || Target;
    };

    // Define gradient backgrounds for slideshow
    const slides = [
        { background: "linear-gradient(45deg, #667eea 0%, #764ba2 100%)" },
        { background: "linear-gradient(45deg, #f093fb 0%, #f5576c 100%)" }
    ];

    // State to track current slide
    const [currentSlide, setCurrentSlide] = useState(0);

    // Auto-advance slideshow
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide(prev => (prev + 1) % slides.length);
        }, 7000);
        return () => clearInterval(timer);
    }, [slides.length]);

    // Render animated background elements
    const renderAnimatedBackgrounds = () => (
        <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
                <motion.div
                    key={i}
                    className="absolute rounded-full bg-white/10"
                    style={{
                        width: Math.random() * 100 + 50,
                        height: Math.random() * 100 + 50,
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`
                    }}
                    animate={{
                        y: [0, -30, 0],
                        opacity: [0.3, 0.7, 0.3]
                    }}
                    transition={{
                        duration: Math.random() * 3 + 2,
                        repeat: Infinity,
                        delay: Math.random() * 2
                    }}
                />
            ))}
        </div>
    );

    // Render feature cards
    const renderFeatures = () => (
        <div className="grid md:grid-cols-3 gap-8">
            {content.features.map((feature, index) => {
                const IconComponent = getIconComponent(feature.icon);
                return (
                    <motion.div
                        key={feature.title}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.2 }}
                        whileHover={{ scale: 1.05 }}
                        className="glass-effect rounded-2xl p-8 text-center group cursor-pointer"
                    >
                        <motion.div
                            className="text-primary mb-4 flex justify-center"
                            whileHover={{ scale: 1.2, rotate: 360 }}
                            transition={{ duration: 0.5 }}
                        >
                            <IconComponent className="w-12 h-12" />
                        </motion.div>
                        <h3 className="text-2xl font-bold text-gray-200 mb-4">
                            {feature.title}
                        </h3>
                        <p className="text-gray-300">{feature.description}</p>
                    </motion.div>
                );
            })}
        </div>
    );

    // Render statistics
    const renderStats = () => (
        <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
            {content.stats.map((stat, index) => (
                <motion.div
                    key={stat.label}
                    whileHover={{ scale: 1.1 }}
                    className="glass-effect rounded-xl p-6"
                >
                    <div className="text-3xl font-bold gradient-text mb-2 drop-shadow-md">
                        {stat.number}
                    </div>
                    <div className="text-primary">{stat.label}</div>
                </motion.div>
            ))}
        </motion.div>
    );

    // Render main title and description
    const renderMainContent = () => (
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
        >
            <h2 className="text-5xl md:text-7xl font-bold gradient-text mb-6 drop-shadow-md">
                {content.mainTitle}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                {content.mainDescription}
            </p>
        </motion.div>
    );

    if (loading) {
        return (
            <section className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando...</p>
                </div>
            </section>
        );
    }

    return (
        <section id="nosotros" className="min-h-screen py-12 md:py-20 relative overflow-hidden"
            style={{ background: slides[currentSlide].background }}>
            {renderAnimatedBackgrounds()}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {renderMainContent()}
                {renderFeatures()}
                {renderStats()}
            </div>
        </section>
    );
};

export default About;